import MuiButton, {
  ButtonProps as MuiButtonProps,
  buttonClasses,
} from '@mui/material/Button';
import { colorChannel } from '@mui/system/colorManipulator';
import React from 'react';

import { useTheme } from './muiTheme/utils';

declare module '@mui/material/Button' {
  interface ButtonPropsColorOverrides {
    neutral: true;
  }

  interface ButtonPropsVariantOverrides {
    borderless: true;
    bordered: true;
    borderedSecondary: true;
    prominent: true;
  }
}

export interface ButtonProps {
  loading?: boolean;
  color?: MuiButtonProps['color'];
  variant?: 'borderless' | 'prominent' | 'bordered' | 'borderedSecondary';
  size?: MuiButtonProps['size'];
  startIcon?: MuiButtonProps['startIcon'];
  endIcon?: MuiButtonProps['endIcon'];
  onClick?: MuiButtonProps['onClick'];
  fullWidth?: MuiButtonProps['fullWidth'];
  type?: MuiButtonProps['type'];
  href?: MuiButtonProps['href'];
  autoFocus?: MuiButtonProps['autoFocus'];

  disabled?: MuiButtonProps['disabled'];
  sx?: MuiButtonProps['sx'];
  children?: MuiButtonProps['children'];
}

export function Button<C extends React.ElementType = 'button'>(
  props: MuiButtonProps<C, { component?: C } & ButtonProps>
) {
  const {
    variant = 'borderless',
    color = 'primary',
    size,
    sx,
    loading,
    children,
    ...otherProps
  } = props;

  const theme = useTheme();

  const styles = {
    transition: 'all 0.3s',
    borderRadius: '50svh',
    [`.${buttonClasses.sizeSmall}`]: {
      height: (theme) => theme.tokens.base.button.small.size,
      borderRadius: (theme) => theme.tokens.base.button.small.radiusRounded,
    },
    [`.${buttonClasses.sizeMedium}`]: {
      height: (theme) => theme.tokens.base.button.medium.size,
      borderRadius: (theme) => theme.tokens.base.button.medium.radiusRounded,
    },
    [`.${buttonClasses.sizeLarge}`]: {
      height: (theme) => theme.tokens.base.button.large.size,
      borderRadius: (theme) => theme.tokens.base.button.large.radiusRounded,
    },
    [`&.MuiButton-prominent`]: {
      bgcolor: `${color}.solid`,
      color: `${color}.contrastText`,
      backgroundImage: 'none',
      boxShadow: 'none',
      border: 'none',
      '&:hover': {
        bgcolor: `${color}.solidHover`,
        outlineColor: 'transparent',
        borderColor: 'transparent',
        boxShadow: 'none',
      },
    },

    [`&.MuiButton-bordered`]: {
      bgcolor: `${color}.backgroundSoft`,
      color: `${color}.text`,
      backgroundImage: 'none',
      boxShadow: 'none',
      border: 'none',
      '&:hover': {
        bgcolor: `${color}.backgroundSoftHover`,
        outlineColor: 'transparent',
        borderColor: 'transparent',
        boxShadow: 'none',
      },
      '&:active': {
        bgcolor: `${color}.backgroundSoftActive`,
      },
    },

    [`&.MuiButton-borderedSecondary`]: {
      bgcolor: `secondary.backgroundSoft`,
      color: `${color}.text`,
      backgroundImage: 'none',
      boxShadow: 'none',
      border: 'none',
      '&:hover': {
        bgcolor: `secondary.backgroundSoftHover`,
        outlineColor: 'transparent',
        borderColor: 'transparent',
        boxShadow: 'none',
      },
      '&:active': {
        bgcolor: `${color}.backgroundSoftActive`,
      },
    },

    [`&.MuiButton-borderless`]: {
      color: `${color}.text`,
      '&:hover': {
        bgcolor: `secondary.backgroundSoft`,
      },
    },

    // Neutral specific styles
    [`&.MuiButton-colorNeutral`]: {
      color: 'text.primary',
    },
    [`&.MuiButton-prominentNeutral`]: {
      color: theme.tokens.palette.lightToDark.light,
      bgcolor: theme.tokens.palette.darkToLight.light,
      ...theme.applyStyles('dark', {
        color: theme.tokens.palette.lightToDark.dark,
        bgcolor: theme.tokens.palette.darkToLight.dark,
      }),
      '&:hover': {
        bgcolor: (theme) => {
          return theme.tokens.palette.darkToLight.light;
        },
        backgroundImage: (theme) => {
          return `linear-gradient(rgba(${colorChannel(theme.tokens.palette.lightToDark.light)} / ${theme.tokens.palette.action.hoverOpacity.light}))`;
        },
        ...theme.applyStyles('dark', {
          color: theme.tokens.palette.lightToDark.dark,
          bgcolor: theme.tokens.palette.darkToLight.dark,
          backgroundImage: `linear-gradient(rgba(${colorChannel(theme.tokens.palette.lightToDark.dark)} / ${theme.tokens.palette.action.hoverOpacity.dark}))`,
        }),
      },
    },

    [`&.${buttonClasses.disabled}`]: {
      bgcolor: `action.disabledBackground`,
      color: `action.disabled`,
    },
    [`&.MuiButton-borderless.${buttonClasses.disabled}`]: {
      bgcolor: `transparent`,
      color: `action.disabled`,
    },

    [`&.${buttonClasses.loading}`]: {
      color: 'transparent',
    },
    [`&.MuiButton-borderless.${buttonClasses.loading}`]: {
      color: `transparent`,
    },
  };

  return (
    <MuiButton
      sx={[styles, ...(Array.isArray(sx) ? sx : [sx])]}
      variant={variant}
      color={color}
      size={size}
      loading={loading}
      {...otherProps}
    >
      {children}
    </MuiButton>
  );
}
